import type { RfidDocumentRow } from '../services/rfid.service';
import { formatDateEnGb, getDocumentPlant } from './document';

export type DocumentListFilterState = {
  search: string;
  filterDocNo: string;
  filterCustomer: string;
  filterPlant: string;
  /** `yyyy-mm-dd` from `<input type="date">`, or empty. */
  filterDate: string;
};

export type FilterOption = { value: string; label: string };

export const EMPTY_DOCUMENT_LIST_FILTERS: DocumentListFilterState = {
  search: '',
  filterDocNo: '',
  filterCustomer: '',
  filterPlant: '',
  filterDate: '',
};

export function hasActiveDocumentListFilters(filters: DocumentListFilterState): boolean {
  const { search, filterDocNo, filterCustomer, filterPlant, filterDate } = filters;
  return Boolean(search || filterDocNo || filterCustomer || filterPlant || filterDate);
}

export function buildPlantFilterOptions(rows: RfidDocumentRow[]): FilterOption[] {
  const set = new Set<string>();
  rows.forEach((d) => {
    const p = getDocumentPlant(d);
    if (p) set.add(p);
  });
  return Array.from(set)
    .sort()
    .map((p) => ({ value: p, label: p }));
}

export function buildCustomerFilterOptions(rows: RfidDocumentRow[]): FilterOption[] {
  const set = new Set<string>();
  rows.forEach((d) => {
    const name = String(d.CustName ?? '').trim();
    if (name) set.add(name);
  });
  return Array.from(set)
    .sort()
    .map((name) => ({ value: name, label: name }));
}

/** Match mobile: date filter uses en-GB `dd/mm/yyyy` against PostingDate. */
export function dateInputToEnGb(isoDate: string): string {
  const trimmed = isoDate.trim();
  if (!trimmed) return '';
  const d = new Date(`${trimmed}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return formatDateEnGb(d);
}

export function applyDocumentListFilters(
  rows: RfidDocumentRow[],
  filters: DocumentListFilterState
): RfidDocumentRow[] {
  let list = rows;
  const { search, filterDocNo, filterCustomer, filterPlant, filterDate } = filters;

  if (search.trim()) {
    const q = search.trim().toLowerCase();
    list = list.filter((row) => JSON.stringify(row).toLowerCase().includes(q));
  }
  if (filterDocNo.trim()) {
    const q = filterDocNo.trim().toLowerCase();
    list = list.filter((row) => String(row.DocumentNo ?? '').toLowerCase().includes(q));
  }
  if (filterCustomer.trim()) {
    const q = filterCustomer.trim().toLowerCase();
    list = list.filter((row) => String(row.CustName ?? '').toLowerCase().includes(q));
  }
  if (filterPlant.trim()) {
    list = list.filter((row) => getDocumentPlant(row) === filterPlant);
  }
  if (filterDate.trim()) {
    const target = dateInputToEnGb(filterDate);
    if (target) {
      list = list.filter((row) => String(row.PostingDate ?? '').includes(target));
    }
  }
  return list;
}
